import React, { forwardRef, useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

function cn(...classes) {
  return classes.filter(Boolean).join(' ');
}

// Animated height wrapper for smooth expand/collapse
function AnimatedHeight({ open, children, className }) {
  const innerRef = useRef(null);
  const [height, setHeight] = useState(open ? 'auto' : 0);
  const [overflow, setOverflow] = useState(open ? 'visible' : 'hidden');

  useEffect(() => {
    const el = innerRef.current;
    if (!el) return;
    if (open) {
      const h = el.scrollHeight;
      setHeight(h);
      setOverflow('hidden');
      const t = setTimeout(() => { setHeight('auto'); setOverflow('visible'); }, 220);
      return () => clearTimeout(t);
    } else {
      const h = el.scrollHeight;
      setHeight(h);
      setOverflow('hidden');
      requestAnimationFrame(() => { requestAnimationFrame(() => setHeight(0)); });
    }
  }, [open]);

  return (
    <div
      style={{
        height: typeof height === 'number' ? `${height}px` : height,
        overflow,
        transition: 'height 200ms cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      <div ref={innerRef} className={className}>
        {children}
      </div>
    </div>
  );
}

const Collapsible = forwardRef(function Collapsible(
  {
    children,
    title,
    defaultOpen = false,
    open: controlledOpen,
    onOpenChange,
    icon,
    className,
    headerClassName,
    contentClassName,
    style,
    disabled = false,
    variant = 'default',
    size = 'md',
    ...rest
  },
  ref
) {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : internalOpen;

  const handleToggle = () => {
    if (disabled) return;
    const newOpen = !isOpen;
    if (!isControlled) setInternalOpen(newOpen);
    onOpenChange?.(newOpen);
  };

  const variantClasses = {
    default: 'border border-gray-200/80 rounded-xl shadow-sm ring-1 ring-black/[0.03] bg-white overflow-hidden',
    minimal: 'overflow-hidden',
    bordered: 'border-b border-gray-200 overflow-hidden',
  };

  const sizeClasses = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
  };

  const containerClasses = cn(
    variantClasses[variant],
    disabled && 'opacity-50 cursor-not-allowed',
    className
  );

  const headerClasses = cn(
    'flex items-center justify-between w-full px-4 py-3.5 cursor-pointer',
    'transition-colors duration-150',
    'hover:bg-gray-50/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-inset',
    sizeClasses[size],
    disabled && 'cursor-not-allowed hover:bg-transparent',
    headerClassName
  );

  return (
    <div ref={ref} className={containerClasses} style={style} {...rest}>
      <button
        type="button"
        onClick={handleToggle}
        className={headerClasses}
        disabled={disabled}
        aria-expanded={isOpen}
      >
        <div className="flex items-center gap-2.5 flex-1 text-left">
          {icon && (
            <span className="flex-shrink-0 text-gray-500">{icon}</span>
          )}
          <span className="font-semibold text-gray-900 tracking-tight">{title}</span>
        </div>
        <span
          className="flex-shrink-0 text-gray-400 transition-transform duration-200 ease-in-out"
          style={{ transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)' }}
        >
          <ChevronDown size={18} strokeWidth={2} />
        </span>
      </button>

      <AnimatedHeight open={isOpen} className={cn('px-4 pb-4 pt-1', contentClassName)}>
        {children}
      </AnimatedHeight>
    </div>
  );
});

Collapsible.displayName = 'Collapsible';

const CollapsibleGroup = forwardRef(function CollapsibleGroup(
  {
    children,
    allowMultiple = false,
    defaultIndex,
    className,
    style,
    ...rest
  },
  ref
) {
  const [openIndexes, setOpenIndexes] = useState(
    defaultIndex !== undefined
      ? Array.isArray(defaultIndex) ? defaultIndex : [defaultIndex]
      : []
  );

  const handleToggle = (index) => {
    setOpenIndexes((prev) => {
      const isOpen = prev.includes(index);
      if (allowMultiple) {
        return isOpen ? prev.filter((i) => i !== index) : [...prev, index];
      }
      return isOpen ? [] : [index];
    });
  };

  return (
    <div ref={ref} className={cn('space-y-2', className)} style={style} {...rest}>
      {React.Children.map(children, (child, index) => {
        if (React.isValidElement(child) && child.type === Collapsible) {
          return React.cloneElement(child, {
            open: openIndexes.includes(index),
            onOpenChange: () => handleToggle(index),
          });
        }
        return child;
      })}
    </div>
  );
});

CollapsibleGroup.displayName = 'CollapsibleGroup';

export { Collapsible, CollapsibleGroup };
export default Collapsible;
